import React from 'react';
import { motion } from 'framer-motion';

const Card = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay }}
    className="w-full rounded-3xl border border-gray-200 bg-white shadow-sm p-8 lg:p-10"
  >
    {children}
  </motion.div>
);

const SupportPage = () => {
  return (
    <main className="min-h-screen bg-white text-gray-900">
      {/* Prominent Hero: dual ribbon gradient + subtle nodes */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -rotate-6 -left-1/3 top-0 w-[160%] h-56 bg-gradient-to-r from-blue-500/15 via-purple-500/15 to-blue-500/15" />
          <div className="absolute rotate-3 -right-1/3 top-28 w-[160%] h-48 bg-gradient-to-r from-purple-500/15 via-blue-500/15 to-purple-500/15" />
          <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 200 100" preserveAspectRatio="none">
            {Array.from({ length: 24 }).map((_, i) => (
              <circle key={i} cx={(i * 9) % 200} cy={(i * 13) % 100} r="0.6" fill="#60a5fa">
                <animate attributeName="r" values="0.4;0.9;0.4" dur={`${1.8 + (i % 5) * 0.25}s`} repeatCount="indefinite" />
              </circle>
            ))}
          </svg>
        </div>
        <div className="container-custom py-24 lg:py-32 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="text-center">
            <h1 className="text-4xl lg:text-6xl font-bold font-slim tracking-tight">FlowMatic — Support</h1>
          </motion.div>
        </div>
      </section>

      <section className="container-custom py-16 lg:py-20">
        <div className="max-w-5xl mx-auto space-y-8">
          <Card>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Overview</span>
            </h2>
            <p className="text-lg leading-relaxed text-gray-700">
              At FlowMatic, we provide dedicated support to ensure your AI agents run smoothly, stay updated, and deliver the best results for your business. Our team is always ready to help you resolve issues, optimize performance, and expand your automation capabilities.
            </p>
          </Card>

          <Card delay={0.05}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Types of Support</span>
            </h2>
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold mb-2">🧠 Technical Support</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Fixing response or logic errors.</li>
                  <li>Troubleshooting integrations (forms, CRMs, call systems).</li>
                  <li>Ensuring system stability and uptime.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">🔄 Agent Maintenance</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Updating the knowledge base as your business evolves.</li>
                  <li>Optimizing performance for better accuracy and speed.</li>
                  <li>Adding new features or automation flows when required.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">📊 Performance Review</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Monthly or quarterly performance reports.</li>
                  <li>Key metrics on engagement, leads, and conversions.</li>
                  <li>Personalized recommendations to enhance results.</li>
                </ul>
              </div>
            </div>
          </Card>

          <Card delay={0.1}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Support Plans</span>
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-gray-700">
                <thead>
                  <tr className="border-b">
                    <th className="py-3 pr-6 font-semibold">Plan</th>
                    <th className="py-3 font-semibold">Includes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-3 pr-6 font-medium">Basic Support</td>
                    <td className="py-3">Email support for bug fixes, minor updates, and troubleshooting.</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 pr-6 font-medium">Premium Support</td>
                    <td className="py-3">Priority assistance, monthly performance reports, and regular optimization.</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-6 font-medium">Enterprise Support</td>
                    <td className="py-3">Full-service management, continuous monitoring, and custom feature development.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          <Card delay={0.15}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Response Time</span>
            </h2>
            <p className="text-gray-700">We aim to respond to all support requests within 24 hours on business days. For urgent production issues or live system interruptions, we prioritize immediate attention.</p>
          </Card>

          <Card delay={0.2}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">How to Contact Us</span>
            </h2>
            <p className="text-gray-700">You can reach our support team anytime for help, updates, or new requests.</p>
            <p className="mt-2 font-medium">📧 support@flowmatic.com</p>
            <p className="text-gray-600">🕒 Mon–Fri, 9:00 AM – 6:00 PM (PKT)</p>
          </Card>

          <Card delay={0.25}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Frequently Asked Questions</span>
            </h2>
            <div className="space-y-6 text-gray-700">
              <div>
                <p className="font-semibold">Q: Can you retrain my agent when we update our pricing or services?</p>
                <p>Yes — we can update your AI agent’s knowledge base anytime your business changes.</p>
              </div>
              <div>
                <p className="font-semibold">Q: What should I do if my agent stops responding or behaves incorrectly?</p>
                <p>Simply email our support team. We’ll investigate and fix the issue promptly.</p>
              </div>
              <div>
                <p className="font-semibold">Q: How often do you update deployed agents?</p>
                <p>We perform routine checks and updates monthly, or as required by your plan.</p>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </main>
  );
};

export default SupportPage;


